import React, { useEffect, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { showToast } from '../../../Components/ToastNotifications';

// Reducer untuk handle state
const initialState = {
  users: null,
  isLoading: true,
  loadingKeys: [],
};

function reducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true };
    case 'FETCH_SUCCESS':
      return { ...state, users: action.payload, isLoading: false };
    case 'FETCH_ERROR':
      return { ...state, isLoading: false };
    case 'UPDATE_START':
      return { ...state, loadingKeys: [...state.loadingKeys, action.key] };
    case 'UPDATE_DONE':
      return {
        ...state,
        loadingKeys: state.loadingKeys.filter((k) => k !== action.key),
      };
    default:
      return state;
  }
}

const RegistrationsDetail = () => {
  const { authUser } = useAuth();
  const role = authUser?.activeSession?.roleCode?.toLowerCase() || 'mentor';
  const [state, dispatch] = useReducer(reducer, initialState);
  const { users, isLoading, loadingKeys } = state;
  const [selectedNote, setSelectedNote] = useState(null);

  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch detail, tanpa spinner penuh saat dipakai untuk refresh diam-diam
  // setelah update status (lihat onHandleUpdateStatusAcceptance).
  const fetchRegistrationsDetail = async (silent = false) => {
    if (!silent) dispatch({ type: 'FETCH_START' });
    const { status, message, data } = await api.getRegistrationsDetail({ id });
    if (status !== 'success') {
      showToast(message, 'danger');
      if (!silent) navigate(-1);
    } else {
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
    }
  };

  useEffect(() => {
    fetchRegistrationsDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Update status acceptance
  const onHandleUpdateStatusAcceptance = async (
    key,
    statusAcceptanceProgramId,
  ) => {
    dispatch({ type: 'UPDATE_START', key });

    const { status } = await api.updateRegistrationAcceptanceStatus({
      id,
      payload: { statusAcceptanceProgramId },
    });

    if (status !== 'success') {
      showToast('Gagal update status!', 'danger');
    }
    // Selalu refresh diam-diam: memilih status "lolos" bisa meng-cascade
    // status TAHAP SELEKSI sebelumnya di backend, jadi tidak cukup toggle
    // satu item lokal.
    await fetchRegistrationsDetail(true);
    dispatch({ type: 'UPDATE_DONE', key });
  };

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center my-5'>
        <div
          className='spinner-border text-primary'
          role='status'
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  if (!users) {
    return <p className='text-center my-5'>❌ Data tidak ditemukan</p>;
  }
  const mentorName = authUser?.fullName || 'Mentor';
  const menteeName = users?.fullName || '';
  const division = users?.institutions?.institutionsDivisionName || '-';
  const institution = users?.institutions?.institutionsName || '-';
  const waMessage = `Halo, Future SDGs Hero!

Terima kasih telah mendaftar sebagai calon peserta magang Campus Leaders Program batch 13 untuk posisi ${division} di ${institution}. 
CV dan data diri yang Anda unggah melalui clp.bcf.or.id telah kami terima. Kami mengirimkan undangan melalui e-mail yang terdaftar untuk mengikuti proses seleksi. 
Silahkan cek kembali e-mail dan akun Anda untuk mengikuti tahapan seleksi.
Terima kasih. 

Salam hangat,
Bakrie Center Foundation`;

  const waReminderMessage = `Waktu pengumpulan skill test dalam rangka seleksi magang Campus Leaders Program batch 13 untuk posisi ${division} di ${institution} sedikit lagi! Batas maksimal mengumpulkan skill test hingga Senin, 29 Juni 2026 via e-mail. Harap mengikuti instruksi yang tertera pada e-mail. 

Pastikan kamu mengikuti proses seleksi dan raih kesempatan untuk menjadi SDGs Hero Campus Leaders Program batch 13. 
Tetap pantau clp.bcf.or.id untuk informasi proses seleksi. 

Salam,
Tim Bakrie Center Foundation`;

  return (
    <div className='container-fluid p-0 pb-5'>
      {/* Premium Header */}
      <div className='card border-0 rounded-4 mb-4 overflow-hidden shadow-sm'>
        <div className='card-body p-4'>
          <div className='d-flex align-items-center gap-3'>
            <button
              onClick={() => navigate(-1)}
              className='btn btn-light rounded-circle d-flex align-items-center justify-content-center border transition-all hover-scale'
              style={{ width: '40px', height: '40px' }}
            >
              <i className='bi bi-arrow-left'></i>
            </button>
            <div>
              <span className='badge bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-pill fw-bold small mb-1'>
                Detil Pendaftaran Mentee
              </span>
              <h1 className='h4 fw-bold mb-0 text-dark'>{users?.fullName}</h1>
            </div>
          </div>
        </div>
      </div>

      <div className='row g-4'>
        {/* Left Content */}
        <div className='col-lg-8'>
          {/* Main Info Groups */}
          <div className='d-grid gap-4'>
            {/* Personal Info */}
            <SectionCard
              title='Informasi Pribadi'
              icon='person-lines-fill'
            >
              <div className='row g-3'>
                <InfoItem
                  label='Nama Lengkap'
                  value={users?.fullName}
                  col='6'
                />
                <InfoItem
                  label='Email'
                  value={users?.email}
                  col='6'
                />
                <div className='col-6'>
                  <label
                    className='small text-muted mb-1 d-block fw-bold text-uppercase'
                    style={{ letterSpacing: '0.025em' }}
                  >
                    No. Handphone
                  </label>
                  <div className='d-flex flex-column gap-2'>
                    <div className='text-dark fw-medium small'>
                      {users?.numberPhone || '-'}
                    </div>
                    {users?.numberPhone && (
                      <div className='d-flex flex-wrap gap-2 mt-1'>
                        <a
                          href={`https://wa.me/${users.numberPhone
                            .replace(/[^\d+]/g, '')
                            .replace(/^\+/, '')
                            .replace(
                              /^0/,
                              '62',
                            )}?text=${encodeURIComponent(waMessage)}`}
                          target='_blank'
                          rel='noreferrer'
                          className='btn btn-success btn-xs d-inline-flex align-items-center gap-1 py-1 px-2 fw-semibold rounded-pill shadow-sm border-0 hover-scale'
                          style={{
                            fontSize: '0.7rem',
                            backgroundColor: '#25D366',
                            color: '#ffffff',
                          }}
                        >
                          <i className='bi bi-whatsapp'></i> Undangan Seleksi
                        </a>
                        <a
                          href={`https://wa.me/${users.numberPhone
                            .replace(/[^\d+]/g, '')
                            .replace(/^\+/, '')
                            .replace(
                              /^0/,
                              '62',
                            )}?text=${encodeURIComponent(waReminderMessage)}`}
                          target='_blank'
                          rel='noreferrer'
                          className='btn btn-warning btn-xs d-inline-flex align-items-center gap-1 py-1 px-2 fw-semibold rounded-pill shadow-sm border-0 hover-scale text-white'
                          style={{
                            fontSize: '0.7rem',
                            backgroundColor: '#ff9800',
                          }}
                        >
                          <i className='bi bi-bell-fill'></i> Reminder Skill Test
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                <InfoItem
                  label='Semester'
                  value={users?.semester}
                  col='6'
                />

                {users?.domicilies?.length > 0 ? (
                  users.domicilies.map((value, idx) => (
                    <React.Fragment key={idx}>
                      <div className='col-12 mt-3 mb-1'>
                        <span className='text-muted small fw-bold text-uppercase'>
                          Domisili ({value.statusAddress})
                        </span>
                      </div>
                      <InfoItem
                        label='Provinsi'
                        value={value?.provinciesName}
                        col='6'
                      />
                      <InfoItem
                        label='Kota/Kabupaten'
                        value={value?.regenciesName}
                        col='6'
                      />
                      <InfoItem
                        label='Alamat Lengkap'
                        value={value?.completeAddress}
                      />
                    </React.Fragment>
                  ))
                ) : (
                  <div className='col-12'>
                    <p className='text-muted small italic'>
                      Tidak ada data domisili
                    </p>
                  </div>
                )}
              </div>
            </SectionCard>

            {/* University & Program */}
            <SectionCard
              title='Akademik & Penempatan'
              icon='mortarboard-fill'
            >
              <div className='row g-3'>
                <InfoItem
                  label='Universitas'
                  value={users?.universities?.universitiesName}
                  col='6'
                />
                <InfoItem
                  label='Program Studi'
                  value={users?.universities?.universitiesMajor}
                  col='6'
                />
                <InfoItem
                  label='Lembaga Mitra'
                  value={users?.institutions?.institutionsName}
                  col='6'
                />
                <InfoItem
                  label='Divisi/Bidang'
                  value={users?.institutions?.institutionsDivisionName}
                  col='6'
                />
                <InfoItem
                  label='Cluster Fokus'
                  value={users?.institutions?.institutionsClusterFocus}
                  col='6'
                />
                <InfoItem
                  label='Lokasi Penempatan'
                  value={`${users?.regenciesUsersLocation || '-'}, ${users?.institutions?.institutionsLocation || '-'}`}
                  col='6'
                />
              </div>
            </SectionCard>

            {/* Documents */}
            <SectionCard
              title='Berkas & Dokumen'
              icon='file-earmark-text-fill'
            >
              <div className='row g-3'>
                <DocItem
                  label='Curriculum Vitae (CV)'
                  url={users?.curriculumVitae}
                />
                <DocItem
                  label='Transkrip Akademik'
                  url={users?.academicTranscript}
                />
                <DocItem
                  label='Portfolio'
                  url={users?.portfolio}
                />
                <DocItem
                  label='Surat Eligibility'
                  url={users?.eligibilityFromUniversity}
                />
                <DocItem
                  label='Sertifikat Organisasi'
                  url={users?.organizationCertificateOne}
                />
              </div>
            </SectionCard>

            {/* SDGs Volunteer */}
            <SectionCard
              title='SDGs Volunteer Details'
              icon='heart-fill'
            >
              <div className='row g-4'>
                <div className='col-md-6 border-end'>
                  <h6 className='fw-bold small text-muted text-uppercase mb-3'>
                    Informed Consent
                  </h6>
                  <div className='d-flex flex-column gap-2'>
                    <ConsentItem
                      label='Online Pre-event'
                      checked={users?.volPreEvent}
                    />
                    <ConsentItem
                      label='Offline Main Event'
                      checked={users?.volMainEvent}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <h6 className='fw-bold small text-muted text-uppercase mb-3'>
                    Tipe Partisipasi
                  </h6>
                  <div className='p-2 rounded-3 bg-light border border-light-subtle d-inline-block px-3'>
                    <span className='fw-bold text-primary small'>
                      {users?.volType || '-'}
                    </span>
                  </div>
                </div>
                <div className='col-12'>
                  <h6 className='fw-bold small text-muted text-uppercase mb-2'>
                    Motivasi & Pengalaman
                  </h6>
                  <div className='p-3 rounded-4 bg-light small mb-3 border border-light-subtle'>
                    <p className='mb-0 fw-medium text-dark lh-base'>
                      {users?.volDescMotivated || 'Tidak ada uraian motivasi'}
                    </p>
                  </div>
                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <span className='small text-muted'>Pengalaman Serupa:</span>
                    <span
                      className={`badge rounded-pill bg-${users?.volHasExperience ? 'success' : 'secondary'} bg-opacity-10 text-${users?.volHasExperience ? 'success' : 'secondary'} border border-${users?.volHasExperience ? 'success' : 'secondary'} border-opacity-25`}
                    >
                      {users?.volHasExperience ? 'Pernah' : 'Belum Pernah'}
                    </span>
                  </div>
                  {users?.volHasExperience && (
                    <div className='p-3 rounded-4 bg-light small border border-light-subtle'>
                      {users?.volDescExperience}
                    </div>
                  )}
                </div>
                <div className='col-12'>
                  <h6 className='fw-bold small text-muted text-uppercase mb-2'>
                    Situational Question (ASQ)
                  </h6>
                  <div className='p-3 rounded-4 bg-light small border border-light-subtle'>
                    {users?.volAsq || 'Belum dijawab'}
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Others & Supervisor */}
            <div className='row g-4'>
              <div className='col-md-6'>
                <SectionCard
                  title='Informasi Tambahan'
                  icon='info-circle-fill'
                >
                  <div className='d-grid gap-3'>
                    {users?.programCode === 'BUMDES' ? (
                      <div>
                        <label className='small text-muted d-block mb-1'>
                          Esai Motivasi
                        </label>
                        <p className='small fw-medium mb-0' style={{ whiteSpace: 'pre-line' }}>
                          {users?.motivationEssay || '-'}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className='small text-muted d-block mb-1'>
                            Tentang BCF
                          </label>
                          <p className='small fw-medium mb-0'>
                            {users?.wdytBcf || '-'}
                          </p>
                        </div>
                        <div>
                          <label className='small text-muted d-block mb-1'>
                            Tentang TBC
                          </label>
                          <p className='small fw-medium mb-0'>
                            {users?.wdytTbc || '-'}
                          </p>
                        </div>
                        <div>
                          <label className='small text-muted d-block mb-1'>
                            Info Pendaftaran
                          </label>
                          <p className='small fw-medium mb-0'>
                            {users?.infoJoiningClp || '-'}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </SectionCard>
              </div>
              <div className='col-md-6'>
                <SectionCard
                  title='Data Supervisor'
                  icon='person-workspace'
                >
                  <div className='d-grid gap-3'>
                    <InfoItem
                      label='Nama Lengkap'
                      value={users?.supervisorFullname}
                    />
                    <InfoItem
                      label='No. Telp'
                      value={users?.supervisorPhoneNumber}
                    />
                    <InfoItem
                      label='Email'
                      value={users?.supervisorEmail}
                    />
                  </div>
                </SectionCard>
              </div>
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className='col-lg-4'>
          <div
            className='sticky-top'
            style={{ top: '1.5rem', zIndex: 10 }}
          >
            <SectionCard
              title='Status Penerimaan'
              icon='patch-check-fill'
            >
              <div className='list-group list-group-flush border-0'>
                {users?.statusAcceptance?.map((value, key) => {
                  const isChecked = Boolean(value.isChecked);
                  const isDisabled = !value.accessMentor && role !== 'admin';
                  const statusColor = isChecked
                    ? value.name.includes('Diterima')
                      ? 'success'
                      : value.name.includes('Ditolak')
                        ? 'danger'
                        : 'primary'
                    : 'secondary';

                  return (
                    <div
                      key={key}
                      className={`list-group-item border-0 rounded-4 mb-2 p-3 transition-all ${isChecked ? `bg-${statusColor} bg-opacity-10 border border-${statusColor} border-opacity-10` : 'bg-light border border-light-subtle opacity-75'}`}
                    >
                      <div className='d-flex align-items-start gap-3'>
                        {loadingKeys.includes(key) ? (
                          <div
                            className='spinner-border spinner-border-sm text-primary mt-1'
                            role='status'
                          ></div>
                        ) : (
                          <div className='form-check'>
                            <input
                              disabled={isDisabled}
                              onChange={() =>
                                onHandleUpdateStatusAcceptance(
                                  key,
                                  value.statusAcceptanceProgramId,
                                )
                              }
                              className={`form-check-input border-${statusColor}`}
                              type='checkbox'
                              checked={isChecked}
                              style={{ width: '1.25rem', height: '1.25rem' }}
                            />
                          </div>
                        )}
                        <div className='flex-grow-1'>
                          <div
                            className={`fw-bold mb-1 text-${statusColor}`}
                            style={{ fontSize: '0.9rem' }}
                          >
                            {value.name}
                          </div>
                          <div className='d-grid gap-1'>
                            <div
                              className='d-flex align-items-center gap-1 text-muted'
                              style={{ fontSize: '0.7rem' }}
                            >
                              <i className='bi bi-clock'></i>
                              <span>{value.lastUpdated || '-'}</span>
                            </div>
                            <div
                              className='d-flex align-items-center gap-1 text-muted'
                              style={{ fontSize: '0.7rem' }}
                            >
                              <i className='bi bi-person-circle'></i>
                              <span>{value.updatedBy || 'System'}</span>
                            </div>
                          </div>
                          {value.note && (
                            <button
                              onClick={() =>
                                setSelectedNote({
                                  title: value.name,
                                  content: value.note,
                                })
                              }
                              className='btn btn-sm btn-light border mt-2 py-1 px-3 rounded-pill small transition-all hover-scale d-flex align-items-center gap-1'
                              style={{ fontSize: '0.75rem' }}
                            >
                              <i className='bi bi-chat-left-text text-primary'></i>
                              <span>Lihat Catatan</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          </div>
        </div>
      </div>

      {/* Note Dialog Box */}
      {selectedNote && (
        <div
          className='modal fade show d-block'
          tabIndex='-1'
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1060,
          }}
          onClick={() => setSelectedNote(null)}
        >
          <div
            className='modal-dialog modal-dialog-centered'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='modal-content border-0 rounded-4 shadow-lg overflow-hidden'>
              <div
                className='modal-header border-0 bg-primary bg-opacity-10 py-3'
                style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}
              >
                <h5 className='modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0'>
                  <i className='bi bi-chat-left-dots-fill text-primary'></i>
                  <span>Detail Catatan</span>
                </h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setSelectedNote(null)}
                ></button>
              </div>
              <div className='modal-body p-4'>
                <div className='mb-3'>
                  <span className='badge bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-pill small fw-bold mb-2'>
                    {selectedNote.title}
                  </span>
                  <div className='p-3 bg-light rounded-4 border border-light-subtle mt-2'>
                    <p
                      className='mb-0 text-dark lh-base'
                      style={{ fontSize: '0.9rem' }}
                    >
                      {selectedNote.content}
                    </p>
                  </div>
                </div>
              </div>
              <div className='modal-footer border-0 p-3 bg-light bg-opacity-50'>
                <button
                  type='button'
                  className='btn btn-primary rounded-pill px-4 fw-bold transition-all hover-scale'
                  style={{ fontSize: '0.85rem' }}
                  onClick={() => setSelectedNote(null)}
                >
                  Selesai
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .hover-scale:hover { transform: scale(1.05); }
        .transition-all { transition: all 0.2s ease; }
        .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .sticky-top { transition: top 0.3s ease; }
      `}</style>
    </div>
  );
};

export default RegistrationsDetail;

/* --- Premium Reusable UI Components --- */
const SectionCard = ({ title, icon, children }) => (
  <div className='card border-0 rounded-4 shadow-sm overflow-hidden mb-3'>
    <div className='card-header bg-white border-0 pt-4 px-4'>
      <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
        <i className={`bi bi-${icon} text-primary`}></i>
        {title}
      </h6>
    </div>
    <div className='card-body p-4'>{children}</div>
  </div>
);

const InfoItem = ({ label, value, col = '12' }) => (
  <div className={`col-${col}`}>
    <label
      className='small text-muted mb-1 d-block fw-bold text-uppercase'
      style={{ letterSpacing: '0.025em' }}
    >
      {label}
    </label>
    <div className='text-dark fw-medium small'>{value || '-'}</div>
  </div>
);

const DocItem = ({ label, url }) => (
  <div className='col-12 col-md-6'>
    <div className='p-3 rounded-4 bg-light border border-light-subtle d-flex align-items-center justify-content-between gap-2 transition-all hover-scale shadow-xs'>
      <div className='d-flex align-items-center gap-2 min-w-0'>
        <i className='bi bi-file-earmark-pdf-fill text-danger fs-5'></i>
        <span className='small fw-bold text-dark text-truncate'>{label}</span>
      </div>
      {url ? (
        <a
          href={url}
          target='_blank'
          rel='noreferrer'
          className='btn btn-outline-primary btn-sm rounded-circle p-1 px-2 border-0'
        >
          <i className='bi bi-eye-fill small'></i>
        </a>
      ) : (
        <span className='badge bg-secondary bg-opacity-10 text-secondary border-0 small'>
          N/A
        </span>
      )}
    </div>
  </div>
);

const ConsentItem = ({ label, checked }) => (
  <div className='d-flex align-items-center gap-2'>
    <div
      className={`rounded-circle d-flex align-items-center justify-content-center ${checked ? 'bg-success text-white' : 'bg-light text-muted border'}`}
      style={{ width: '20px', height: '20px' }}
    >
      <i className={`bi ${checked ? 'bi-check-lg' : 'bi-dash'} small`}></i>
    </div>
    <span className={`small ${checked ? 'fw-bold text-dark' : 'text-muted'}`}>
      {label}
    </span>
  </div>
);
